import { QueryHookOptions } from '@apollo/client';
import { useApiLazyQuery } from '@common/hooks';
import DataService from '@core/services/DataService';
import {
  downloadPolicyMedicalPremiumDetails,
  downloadPolicyTravelPremiumDetails
} from '@features/production/policy';
import { getGenericLine, getLineCategory } from '@utils/helper-utils';

import {
  downloadProposalGroupMedicalPremiumDetails,
  downloadProposalMedicalPremiumDetails,
  downloadProposalTravelPremiumDetails
} from '../services';

import { GenericLines, LineCategory } from 'src/enums';

type EntityType = 'proposal' | 'policy';

type PremiumDetailsDownloadType =
  | 'groupMedicalProposal'
  | 'proposalMedical'
  | 'policyMedical'
  | 'proposalTravel'
  | 'policyTravel';

interface DownloadPremiumDetailsVariables {
  proposalId?: string;
  policyID?: string;
}

const downloadFieldByType: Record<PremiumDetailsDownloadType, string> = {
  groupMedicalProposal: 'downloadGroupProposalInsuredPolicyQuoteMedical',
  proposalMedical: 'downloadProposalInsuredPolicyQuoteMedical',
  policyMedical: 'downloadPolicyInsuredPolicyQuoteMedical',
  proposalTravel: 'downloadProposalInsuredPolicyQuoteTravel',
  policyTravel: 'downloadPolicyInsuredPolicyQuoteTravel'
};

export function useDownloadPremiumDetails(
  lineCode: string,
  type: EntityType,
  isCertificate: boolean,
  options?: QueryHookOptions<any, DownloadPremiumDetailsVariables>
) {
  const lineCategory = getLineCategory(lineCode);
  const isGroupMedical =
    getGenericLine(lineCode) === GenericLines.MEDICAL_GROUP;

  const queryType: PremiumDetailsDownloadType =
    isGroupMedical && type === 'proposal'
      ? 'groupMedicalProposal'
      : lineCategory === LineCategory.Medical && type === 'proposal'
        ? 'proposalMedical'
        : (lineCategory === LineCategory.Medical ||
              (isGroupMedical && isCertificate)) &&
            type === 'policy'
          ? 'policyMedical'
          : lineCategory === LineCategory.Travel && type === 'proposal'
            ? 'proposalTravel'
            : lineCategory === LineCategory.Travel && type === 'policy'
              ? 'policyTravel'
              : 'proposalMedical';

  const selectQuery = (): any => {
    switch (queryType) {
      case 'groupMedicalProposal':
        return downloadProposalGroupMedicalPremiumDetails;
      case 'proposalMedical':
        return downloadProposalMedicalPremiumDetails;
      case 'policyMedical':
        return downloadPolicyMedicalPremiumDetails;
      case 'proposalTravel':
        return downloadProposalTravelPremiumDetails;
      case 'policyTravel':
        return downloadPolicyTravelPremiumDetails;
      default:
        return downloadProposalMedicalPremiumDetails;
    }
  };

  const selectedQuery = selectQuery();
  const downloadField = downloadFieldByType[queryType];

  const [downloadApi] = useApiLazyQuery<any, DownloadPremiumDetailsVariables>(
    selectedQuery,
    'downloadPremiumDetails',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    },
    { withLoader: false }
  );

  const downloadDocument = async (
    variables?: DownloadPremiumDetailsVariables
  ): Promise<void | null> => {
    if (!selectedQuery) return null;

    try {
      const response = await downloadApi({ variables });
      const file = response?.data?.production?.[downloadField];

      if (!file?.content) return null;

      DataService.downloadBase64File(
        file.content,
        file.contentType,
        file.fileName
      );
    } catch (err) {
      console.error('Error downloading document:', err);

      return null;
    }
  };

  return downloadDocument;
}
